import { useEffect, useState } from 'react'
import { getAccessToken, getUserToken, getContrato, getIdUsuario } from '@/auth/auth.utils'
import { getMenu } from '@/services/menu.service'

const TOKEN_POLL_INTERVAL_MS = 200
const TOKEN_POLL_MAX_ATTEMPTS = 15 // 3 segundos

/**
 * Normaliza la respuesta del backend para que coincida con lo que espera Menu.jsx.
 * Backend: descripcion, hijos, id, url, orden
 * Menu:    nombre, children, id, url, orden
 */
function normalizeMenuNodes(nodes) {
  if (!Array.isArray(nodes)) return []
  return nodes.map((node) => ({
    id: node.id,
    nombre: node.descripcion,
    url: node.url,
    orden: node.orden,
    children: node.hijos ? normalizeMenuNodes(node.hijos) : undefined,
  }))
}

// Cache de promesa a nivel módulo: StrictMode monta/desmonta/monta el efecto,
// pero la promesa compartida asegura una sola llamada a la API.
// El hook se suscribe con .then() y el flag cancelled decide cuál actualiza el estado.
let menuPromise = null

/**
 * Hook para cargar el menú de navegación desde clientes_service.
 *
 * @returns {{
 *   items: import("../../ds/Menu/Menu.types").MenuNode[],
 *   loading: boolean,
 *   error: Error | null
 * }}
 */
export function useMenu() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    if (!menuPromise) {
      menuPromise = (async () => {
        // Esperar token si no está disponible
        for (let attempt = 0; attempt <= TOKEN_POLL_MAX_ATTEMPTS; attempt++) {
          if (getAccessToken() || getUserToken()) break
          await new Promise((r) => setTimeout(r, TOKEN_POLL_INTERVAL_MS))
        }
        return getMenu({
          idUsuario: getIdUsuario(),
          contrato: getContrato(),
        })
      })()
    }

    menuPromise
      .then((data) => {
        if (!cancelled) {
          setItems(normalizeMenuNodes(data))
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[useMenu] Error al cargar el menú:', err)
          setError(err)
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  return { items, loading, error }
}
